import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useT } from "@/i18n/I18nProvider";
import LanguageToggle from "@/components/LanguageToggle";
import Logo from "@/components/Logo";

export default function Navbar() {
    const { t } = useT();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    const NAV_LINKS = [
        { to: "/", label: t("nav.home"), testId: "nav-home" },
        { to: "/biographie", label: "Biographie", testId: "nav-bio" },
        { to: "/menu", label: "Menu", testId: "nav-menu" },
        { to: "/confort-et-outils", label: "Confort & outils", testId: "nav-comfort" },
        { to: "/massage-domicile", label: "À domicile", testId: "nav-home-massage" },
        { to: "/massage-entreprise", label: "Entreprise", testId: "nav-corporate" },
        { to: "/evenements", label: "Événements", testId: "nav-events" },
        { to: "/blog", label: "Blog", testId: "nav-blog" },
        { to: "/cartes-cadeaux", label: "Cartes-cadeaux", testId: "nav-gift" },
        { to: "/exercices", label: t("nav.exercices"), testId: "nav-exercices" },
        { to: "/#contact", label: t("nav.contact"), testId: "nav-contact" },
    ];

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);
    useEffect(() => setOpen(false), [location]);

    return (
        <header data-testid="main-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "bg-[#FAF7F2]/85 backdrop-blur-xl border-b border-[#E8E2D9]" : "bg-transparent"}`}>
            <nav className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
                <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
                    <Logo size={40} />
                    <span className="font-display text-xl md:text-2xl text-[#2C2A29] tracking-tight">
                        Jérémy Tessier
                        <span className="block text-[10px] tracking-[0.3em] uppercase text-[#6E6862] -mt-1">
                            {t("nav.brand_sub")}
                        </span>
                    </span>
                </Link>

                <ul className="hidden md:flex items-center gap-8">
                    {NAV_LINKS.map((l) => (
                        <li key={l.testId}>
                            <a href={l.to} data-testid={l.testId}
                                className="text-sm text-[#2C2A29] hover:text-[#E07A5F] transition-colors duration-300">
                                {l.label}
                            </a>
                        </li>
                    ))}
                </ul>

                <div className="hidden md:flex items-center gap-3">
                    <LanguageToggle />
                    <a href="/#reservation">
                        <Button data-testid="navbar-cta-book"
                            className="rounded-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white px-6 py-5 transition-transform hover:-translate-y-0.5">
                            {t("nav.book")}
                        </Button>
                    </a>
                </div>

                <div className="md:hidden flex items-center gap-2">
                    <LanguageToggle />
                    <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)}
                        className="p-2 text-[#2C2A29]" aria-label="Menu">
                        {open ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </nav>

            {open && (
                <div data-testid="mobile-menu" className="md:hidden bg-[#FAF7F2] border-t border-[#E8E2D9] px-6 py-6 space-y-4">
                    {NAV_LINKS.map((l) => (
                        <a key={l.testId} href={l.to} data-testid={`mobile-${l.testId}`}
                            className="block text-base text-[#2C2A29] hover:text-[#E07A5F]">
                            {l.label}
                        </a>
                    ))}
                    <a href="/#reservation">
                        <Button className="w-full rounded-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white">
                            {t("nav.book")}
                        </Button>
                    </a>
                </div>
            )}
        </header>
    );
}
